import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { useLocation, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';

import { addDateTime } from '../../../../common/date-time';
import {
    getReseLeakMonitoringMaterialTypeInit,
    getReseLeakMonitoringMaterialDurationInit,
    postReseLeakMonitoringInformationInit,
    resetReseLeakMonitoringSubmitStatus,
} from '../redux/actions';
import {
    CHARGE_SIZE_THRESHOLD,
    CHARGE_SIZE_MIN,
    CHARGE_SIZE_MAX,
    CHARGE_SIZE_RANGE_INVALID_MESSAGE,
    MONITORING_START_DATE_FORMAT,
    MONITORING_START_DATE_FLOOR,
    MONITORING_START_DATE_INVALID_MESSAGE,
    MONITORING_START_DATE_TOO_OLD_MESSAGE,
    MONITORING_START_DATE_INVALID_FORMAT_MESSAGE,
} from '../constants';
import './ReseLeakDetectionMonitoringRequest.scss';

const isChargeSizeHigh = (chargeSize) =>
    chargeSize !== '' && chargeSize !== null && chargeSize !== undefined && Number(chargeSize) >= CHARGE_SIZE_THRESHOLD;

const getMinValidStartDate = () => {
    const oneYearAgo = addDateTime("year", -1, new Date());
    return MONITORING_START_DATE_FLOOR > oneYearAgo ? MONITORING_START_DATE_FLOOR : oneYearAgo;
};

const ReseLeakDetectionMonitoringRequest = () => {

    const [monitoringInfo, setMonitoringInfo] = useState({
        division: '',
        storeId: '',
        assetName: ''
    });
    const [submitConfirmDialog, setSubmitConfirmDialog] = useState(false);
    const [requiredFromUrl, setRequiredFromUrl] = useState({
        materialType: false,
        chargeSize: false,
        monitoringStartDate: false,
        monitoringDuration: false
    });
    const [urlDefaults, setUrlDefaults] = useState({
        materialType: '',
        chargeSize: '',
        monitoringStartDate: null,
        monitoringDuration: ''
    });

    const materialTypeData = useSelector((state) => state.reseLeakDetectionMonitoring.reseLeakMonitoringMaterialTypeData);
    const materialDurationData = useSelector((state) => state.reseLeakDetectionMonitoring.reseLeakMonitoringMaterialDurationData);
    const submitSuccess = useSelector((state) => state.reseLeakDetectionMonitoring.submitSuccess);
    const submitError = useSelector((state) => state.reseLeakDetectionMonitoring.submitError);

    // Transform dropdown options to label/value format for PrimeReact. Both
    // fields are strict select-only dropdowns (no free-typing) - if the URL
    // supplies a value that isn't among the API-provided options, it is
    // injected as an additional selectable option so it still displays
    // correctly and can be kept/re-selected, without allowing arbitrary text
    // entry.
    const materialTypeOptions = useMemo(() => {
        const options = (!materialTypeData || materialTypeData.length === 0)
            ? []
            : materialTypeData.map(type => (typeof type === 'string' ? { label: type, value: type } : type));
        if (urlDefaults.materialType && !options.some((option) => option.value === urlDefaults.materialType)) {
            options.push({ label: urlDefaults.materialType, value: urlDefaults.materialType });
        }
        return options;
    }, [materialTypeData, urlDefaults.materialType]);

    const materialDurationOptions = useMemo(() => {
        const options = (!materialDurationData || materialDurationData.length === 0)
            ? []
            : materialDurationData.map(duration => (typeof duration === 'string' ? { label: duration, value: duration } : duration));
        if (urlDefaults.monitoringDuration && !options.some((option) => String(option.value) === String(urlDefaults.monitoringDuration))) {
            options.push({ label: String(urlDefaults.monitoringDuration), value: urlDefaults.monitoringDuration });
        }
        return options;
    }, [materialDurationData, urlDefaults.monitoringDuration]);

    // Only these option values (API-provided + the URL-sourced value, if any)
    // are ever selectable - kept as a defense-in-depth validation check even
    // though the dropdowns no longer allow free-typing.
    const materialTypeAllowedValues = useMemo(() => new Set([
        ...materialTypeOptions.map((option) => option.value),
        ...(urlDefaults.materialType ? [urlDefaults.materialType] : [])
    ]), [materialTypeOptions, urlDefaults.materialType]);

    const monitoringDurationAllowedValues = useMemo(() => new Set([
        ...materialDurationOptions.map((option) => String(option.value)),
        ...(urlDefaults.monitoringDuration ? [String(urlDefaults.monitoringDuration)] : [])
    ]), [materialDurationOptions, urlDefaults.monitoringDuration]);

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const toast = useRef(null);

    const defaultState = useMemo(() => ({
        materialType: urlDefaults.materialType,
        chargeSize: urlDefaults.chargeSize,
        monitoringStartDate: urlDefaults.monitoringStartDate,
        monitoringDuration: urlDefaults.monitoringDuration,
        notes: ''
    }), [urlDefaults]);

    const {
        values,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        handleSubmit,
        errors,
        resetForm,
        submitCount
    } = useFormik({
        enableReinitialize: true,
        validateOnMount: true,
        initialValues: defaultState,
        validate: (formValues) => {
            const validationErrors = {};
            const chargeSizeHigh = isChargeSizeHigh(formValues.chargeSize);

            if (requiredFromUrl.materialType && !(formValues.materialType && formValues.materialType.trim())) {
                validationErrors.materialType = "Material Type is required";
            } else if (formValues.materialType && !materialTypeAllowedValues.has(formValues.materialType)) {
                validationErrors.materialType = "Material Type must be selected from the dropdown or match the URL value";
            }

            if (requiredFromUrl.chargeSize && (formValues.chargeSize === '' || formValues.chargeSize === null || formValues.chargeSize === undefined)) {
                validationErrors.chargeSize = "Charge Size is required";
            } else if (formValues.chargeSize !== '' && formValues.chargeSize !== null && formValues.chargeSize !== undefined) {
                const chargeSizeValue = Number(formValues.chargeSize);
                if (chargeSizeValue < CHARGE_SIZE_MIN || chargeSizeValue > CHARGE_SIZE_MAX) {
                    validationErrors.chargeSize = CHARGE_SIZE_RANGE_INVALID_MESSAGE;
                }
            }

            if (!chargeSizeHigh) {
                if (requiredFromUrl.monitoringStartDate && !formValues.monitoringStartDate) {
                    validationErrors.monitoringStartDate = "Monitoring Start Date is required";
                }
                if (formValues.monitoringStartDate) {
                    const isValidDateValue = formValues.monitoringStartDate instanceof Date && !isNaN(formValues.monitoringStartDate);
                    if (!isValidDateValue) {
                        validationErrors.monitoringStartDate = MONITORING_START_DATE_INVALID_FORMAT_MESSAGE;
                    } else {
                        const selectedStartDate = formValues.monitoringStartDate;
                        const oneYearAgo = addDateTime("year", -1, new Date());
                        if (selectedStartDate < MONITORING_START_DATE_FLOOR) {
                            validationErrors.monitoringStartDate = MONITORING_START_DATE_INVALID_MESSAGE;
                        } else if (selectedStartDate < oneYearAgo) {
                            validationErrors.monitoringStartDate = MONITORING_START_DATE_TOO_OLD_MESSAGE;
                        }
                    }
                }
                if (requiredFromUrl.monitoringDuration && !formValues.monitoringDuration) {
                    validationErrors.monitoringDuration = "Monitoring Duration is required";
                } else if (formValues.monitoringDuration && !/^\d+$/.test(String(formValues.monitoringDuration))) {
                    validationErrors.monitoringDuration = "Monitoring Duration must be a whole number";
                } else if (formValues.monitoringDuration && !monitoringDurationAllowedValues.has(String(formValues.monitoringDuration))) {
                    validationErrors.monitoringDuration = "Monitoring Duration must be selected from the dropdown or match the URL value";
                }
            }
            return validationErrors;
        },
        onSubmit: () => {
            setSubmitConfirmDialog(true);
        }
    });

    // Only treat Charge Size as "high" (threshold reached) when it is also a
    // valid, in-range value. An out-of-range value (e.g. > 5000 or 0) already
    // shows its own range error - showing the disabled-fields note on top of
    // that would be confusing, so we suppress it until the value is valid.
    const chargeSizeInValidRange = values.chargeSize !== '' && values.chargeSize !== null && values.chargeSize !== undefined
        && Number(values.chargeSize) >= CHARGE_SIZE_MIN && Number(values.chargeSize) <= CHARGE_SIZE_MAX;
    const chargeSizeHigh = isChargeSizeHigh(values.chargeSize) && chargeSizeInValidRange;

    // Division/Store ID/Asset Name are sourced from the URL and are always
    // required - shown/enforced immediately (not gated behind a submit
    // attempt) since there is no user input to "touch" for these fields.
    const monitoringInfoMissing = !monitoringInfo.division || !monitoringInfo.storeId || !monitoringInfo.assetName;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const division = params.get('division') || '';
        const storeId = params.get('storeId') || '';
        const assetName = params.get('assetName') || '';
        setMonitoringInfo({ division, storeId, assetName });

        const materialTypeRaw = params.get('materialType');
        // Reject whitespace-only values - treat them as not provided.
        const materialTypeParam = materialTypeRaw && materialTypeRaw.trim() ? materialTypeRaw : '';
        const chargeSizeParam = params.get('chargeSize');
        const monitoringStartDateParam = params.get('monitoringStartDate');
        const monitoringDurationRaw = params.get('monitoringDuration');
        // Only accept the value if it is strictly a positive integer
        // (e.g. "90 Days" or "90.5" are rejected and treated as not provided).
        const monitoringDurationParam = monitoringDurationRaw && /^\d+$/.test(monitoringDurationRaw)
            ? monitoringDurationRaw
            : '';

        setRequiredFromUrl({
            materialType: !!materialTypeParam,
            chargeSize: !!chargeSizeParam,
            monitoringStartDate: !!monitoringStartDateParam,
            monitoringDuration: !!monitoringDurationParam
        });

        // Strictly parse the URL date against the expected format. If it
        // doesn't parse cleanly, keep the raw string so it can still be
        // shown to the user (via the Calendar's keepInvalid prop) alongside
        // a validation error, instead of silently discarding/accepting it.
        const parsedStartDate = monitoringStartDateParam
            ? Moment(monitoringStartDateParam, MONITORING_START_DATE_FORMAT, true)
            : null;
        const monitoringStartDateValue = !monitoringStartDateParam
            ? null
            : parsedStartDate.isValid()
                ? parsedStartDate.toDate()
                : monitoringStartDateParam;

        setUrlDefaults({
            materialType: materialTypeParam || '',
            chargeSize: chargeSizeParam ? Math.min(Number(chargeSizeParam), CHARGE_SIZE_MAX) : '',
            monitoringStartDate: monitoringStartDateValue,
            monitoringDuration: monitoringDurationParam || ''
        });
    }, [location.search]);

    useEffect(() => {
        dispatch(getReseLeakMonitoringMaterialTypeInit());
        dispatch(getReseLeakMonitoringMaterialDurationInit());
    }, [dispatch]);

    // When options load AND URL values exist, set them in the form
    useEffect(() => {
        if (materialTypeOptions.length > 0 && urlDefaults.materialType) {
            setFieldValue('materialType', urlDefaults.materialType);
        }
    }, [materialTypeOptions, urlDefaults.materialType, setFieldValue]);


    useEffect(() => {
        if (materialDurationOptions.length > 0 && urlDefaults.monitoringDuration) {
            setFieldValue('monitoringDuration', urlDefaults.monitoringDuration);
        }
    }, [materialDurationOptions, urlDefaults.monitoringDuration, setFieldValue]);

    // Charge Size >= threshold disables & nulls out Start Date / Duration
    useEffect(() => {
        if (chargeSizeHigh) {
            if (values.monitoringStartDate) setFieldValue('monitoringStartDate', null);
            if (values.monitoringDuration) setFieldValue('monitoringDuration', '');
        }
    }, [chargeSizeHigh, values.monitoringStartDate, values.monitoringDuration, setFieldValue]);

    useEffect(() => {
        if (submitSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Leak Detection Monitoring information submitted successfully', life: 3000 });
            // Reset to a blank state (not clearForm/resetForm's default
            // behavior, which would revert to initialValues - i.e. the
            // original URL-sourced defaults). Reverting to those would
            // briefly flash the pre-edit URL values on screen during the
            // pause before navigating away, even if the user had changed
            // them prior to submitting.
            resetForm({
                values: {
                    materialType: '',
                    chargeSize: '',
                    monitoringStartDate: null,
                    monitoringDuration: '',
                    notes: ''
                }
            });
            dispatch(resetReseLeakMonitoringSubmitStatus());
            setTimeout(() => {
                history.push('/admin/rese/leak-detection-monitoring/monitoring-request-logs');
            }, 2000);
        } else if (submitError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Request failed due to an internal error. If the issue persists, contact support.', life: 3000 });
            dispatch(resetReseLeakMonitoringSubmitStatus());
        }
    }, [submitSuccess, submitError]);

    useEffect(() => () => {
        dispatch(resetReseLeakMonitoringSubmitStatus());
    }, []);

    const submitHandler = () => {
        if (monitoringInfoMissing) {
            return;
        }
        const payload = {
            division: monitoringInfo.division,
            store_id: monitoringInfo.storeId,
            asset_name: monitoringInfo.assetName,
            material_type: values.materialType && values.materialType.trim() ? values.materialType.trim() : null,
            charge_size: (values.chargeSize === '' || values.chargeSize === null || values.chargeSize === undefined) ? null : Number(values.chargeSize),
            monitoring_date_start: chargeSizeHigh ? null : (values.monitoringStartDate ? Moment(values.monitoringStartDate).format("YYYY-MM-DD") : null),
            monitoring_duration: chargeSizeHigh ? null : (values.monitoringDuration ? Number(values.monitoringDuration) : null),
            notes: values.notes || null
        };
        dispatch(postReseLeakMonitoringInformationInit(payload));
    };

    const rejectHandler = () => {
        setSubmitConfirmDialog(false);
    };

    const clearForm = () => {
        resetForm();
    };

    const requiredLabel = (label, isRequired) => (isRequired ? `${label} *` : label);

    const messageTemplate = () => (
        <div className="confirmDialog-content">
            <div>
                <p>Submit Leak Detection Monitoring configuration for:</p>
            </div>
            <div className='row input-field'>
                <div className='col-12 col-lg-5'><label>Division</label></div>
                <div className='col-12 col-lg-7'><strong>{monitoringInfo.division}</strong></div>
            </div>
            <div className='row input-field'>
                <div className='col-12 col-lg-5'><label>Store ID</label></div>
                <div className='col-12 col-lg-7'><strong>{monitoringInfo.storeId}</strong></div>
            </div>
            <div className='row input-field'>
                <div className='col-12 col-lg-5'><label>Asset Name</label></div>
                <div className='col-12 col-lg-7'><strong>{monitoringInfo.assetName}</strong></div>
            </div>
        </div>
    );

    const monitoringInfoHeader = (
        <div className="card-header-custom">
            <i className="pi pi-info-circle"></i>
            <span>Monitoring Information</span>
        </div>
    );

    const monitoringConfigHeader = (
        <div className="card-header-custom">
            <i className="pi pi-cog"></i>
            <span>Monitoring Configuration</span>
        </div>
    );

    return (
        <div className="rese-leak-detection-monitoring-request-container">
            <Toast ref={toast} position="bottom-center" />
            <div className="cards-container">
                {/* Monitoring Information Card - read-only, sourced from URL only */}
                <Card header={monitoringInfoHeader} className="info-card">
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Division</span>
                            <span className="info-value">{monitoringInfo.division || 'No data'}</span>
                            {!monitoringInfo.division && <span className="p-error">Division is required</span>}
                        </div>
                        <div className="info-item">
                            <span className="info-label">Store ID</span>
                            <span className="info-value">{monitoringInfo.storeId || 'No data'}</span>
                            {!monitoringInfo.storeId && <span className="p-error">Store ID is required</span>}
                        </div>
                        <div className="info-item">
                            <span className="info-label">Asset Name</span>
                            <span className="info-value">{monitoringInfo.assetName || 'No data'}</span>
                            {!monitoringInfo.assetName && <span className="p-error">Asset Name is required</span>}
                        </div>
                    </div>
                </Card>

                {/* Monitoring Configuration Card */}
                <Card header={monitoringConfigHeader} className="config-card">
                    <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                        <ConfirmDialog group="declarative" style={{ width: "40%" }} visible={submitConfirmDialog} onHide={() => setSubmitConfirmDialog(false)}
                            message={messageTemplate}
                            header="Leak Detection Monitoring Confirmation" accept={submitHandler} reject={rejectHandler} />

                        <div className="config-grid">
                            <div className="config-row-2">
                                <div className="form-field">
                                    <label htmlFor="materialType">{requiredLabel("Material Type", requiredFromUrl.materialType)}</label>
                                    <Dropdown
                                        id="materialType"
                                        name="materialType"
                                        options={materialTypeOptions}
                                        value={values.materialType}
                                        onChange={(e) => setFieldValue('materialType', e.value)}
                                        placeholder="Select Material Type"
                                        emptyMessage="No values"
                                        showClear
                                        className="w-full"
                                    />
                                    {(touched.materialType || submitCount > 0) && errors.materialType && <span className="p-error">{errors.materialType}</span>}
                                </div>

                                <div className="form-field">
                                    <label htmlFor="chargeSize">{requiredLabel("Charge Size", requiredFromUrl.chargeSize)}</label>
                                    <InputNumber
                                        id="chargeSize"
                                        name="chargeSize"
                                        value={values.chargeSize === '' ? null : values.chargeSize}
                                        onValueChange={(e) => {
                                            let newValue = e.value;
                                            if (newValue !== null && newValue !== undefined && newValue > CHARGE_SIZE_MAX) {
                                                newValue = CHARGE_SIZE_MAX;
                                            }
                                            setFieldValue('chargeSize', newValue === null ? '' : newValue);
                                        }}
                                        onBlur={() => setFieldTouched('chargeSize', true)}
                                        useGrouping={false}
                                        min={CHARGE_SIZE_MIN}
                                        maxLength={4}
                                        placeholder="Enter Charge Size"
                                        className="w-full"
                                    />
                                    {(touched.chargeSize || submitCount > 0) && errors.chargeSize && <span className="p-error">{errors.chargeSize}</span>}
                                </div>
                            </div>

                            <div className="config-row-2">
                                <div className="form-field">
                                    <label htmlFor="monitoringStartDate">{requiredLabel("Monitoring Start Date", requiredFromUrl.monitoringStartDate && !chargeSizeHigh)}</label>
                                    <Calendar
                                        id="monitoringStartDate"
                                        name="monitoringStartDate"
                                        value={values.monitoringStartDate}
                                        onChange={(e) => setFieldValue("monitoringStartDate", e.value)}
                                        dateFormat="mm-dd-yy"
                                        placeholder="MM-DD-YYYY"
                                        minDate={getMinValidStartDate()}
                                        disabled={chargeSizeHigh}
                                        readOnlyInput
                                        keepInvalid
                                        showIcon
                                        className="w-full"
                                    />
                                    {errors.monitoringStartDate && <span className="p-error">{errors.monitoringStartDate}</span>}
                                </div>

                                <div className="form-field">
                                    <label htmlFor="monitoringDuration">{requiredLabel("Monitoring Duration (Days)", requiredFromUrl.monitoringDuration && !chargeSizeHigh)}</label>
                                    <Dropdown
                                        id="monitoringDuration"
                                        name="monitoringDuration"
                                        options={materialDurationOptions}
                                        value={values.monitoringDuration}
                                        onChange={(e) => setFieldValue('monitoringDuration', e.value === null ? '' : e.value)}
                                        placeholder="Select Monitoring Duration"
                                        emptyMessage="No values"
                                        disabled={chargeSizeHigh}
                                        showClear
                                        className="w-full"
                                    />
                                    {(touched.monitoringDuration || submitCount > 0) && errors.monitoringDuration && <span className="p-error">{errors.monitoringDuration}</span>}
                                </div>
                            </div>
                            {chargeSizeHigh && (
                                <p className="charge-size-note">
                                    Charge size is {CHARGE_SIZE_THRESHOLD} or greater, so monitoring start date and monitoring duration are disabled.
                                </p>
                            )}

                            <div className="form-field">
                                <label htmlFor="notes">Notes</label>
                                <InputTextarea
                                    id="notes"
                                    name="notes"
                                    value={values.notes}
                                    onChange={handleChange}
                                    rows={3}
                                    autoResize
                                    placeholder="Add notes (optional)"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="button-group">
                            <Button label="Submit" type="submit" icon="pi pi-check" disabled={monitoringInfoMissing || !!errors.chargeSize || !!errors.monitoringStartDate || !!errors.materialType || !!errors.monitoringDuration} />
                            <Button label="Clear" type="button" onClick={clearForm} className="p-button-secondary" icon="pi pi-times" />
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default ReseLeakDetectionMonitoringRequest;
